/**
 * Isolated Foundation Tool - BIM 2025 Grade
 * 
 * Point-based foundation placement with:
 * - Pad footings (rectangular, square, circular)
 * - Pile caps (2, 3, 4, 6, 9+ piles)
 * - Stepped footings (multi-tier)
 * - Pedestal/stub column integration
 * - Dowel placement
 * - Soil bearing capacity checks
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, StructuralMaterial, RebarHostData, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

// ==================== Foundation Types ====================

export type FoundationType =
    | 'Pad Footing'
    | 'Pile Cap'
    | 'Stepped Footing'
    | 'Pedestal'
    | 'Combined Footing';

export type FootingShape = 'Rectangular' | 'Square' | 'Circular';

export type PileConfiguration =
    | '2-Pile'      // 2 piles in line
    | '3-Pile'      // Triangle
    | '4-Pile'      // Square
    | '6-Pile'      // 2x3 pattern
    | '9-Pile'      // 3x3 pattern
    | 'Custom';

// ==================== Pile Data ====================

export interface PileData {
    configuration: PileConfiguration;
    pileType: string;           // e.g., "Bored Pile 600mm"
    pileDiameter: number;       // mm
    pileLength: number;         // m
    pileSpacing: number;        // mm center-to-center
    edgeDistance: number;       // mm from pile center to cap edge
    pilePositions: [number, number][]; // Local XY positions relative to cap center
}

// ==================== Stepped Footing ====================

export interface StepData {
    width: number;              // mm
    length: number;             // mm
    thickness: number;          // mm
    offset: number;             // mm above base
}

export interface SteppedFootingData {
    steps: StepData[];          // From bottom to top (excluding base)
}

// ==================== Pedestal/Stub Column ====================

export interface PedestalData {
    enabled: boolean;
    width: number;              // mm
    length: number;             // mm
    height: number;             // mm
    shape: 'Rectangular' | 'Circular';
}

// ==================== Dowels ====================

export interface DowelData {
    enabled: boolean;
    barSize: string;            // e.g., "Ø20"
    barDiameter: number;        // mm
    quantity: number;
    embedmentDepth: number;     // mm into footing
    projectionHeight: number;   // mm above footing
    arrangement: 'Square' | 'Circular';
    spacing: number;            // mm between bars
}

// ==================== Enhanced Geometry ====================

export interface EnhancedFoundationGeometryData {
    // Type
    foundationType: FoundationType;

    // Position
    position: [number, number, number];
    rotation: number;           // degrees

    // Base dimensions
    shape: FootingShape;
    width: number;              // mm (X)
    length: number;             // mm (Y)
    thickness: number;          // mm (Z)
    topElevation: number;       // m
    bottomElevation: number;    // m

    // Level reference
    levelId: string;
    heightOffset: number;       // mm from level

    // Pile cap data (if applicable)
    pileData?: PileData;

    // Stepped footing data
    steppedData?: SteppedFootingData;

    // Pedestal
    pedestalData?: PedestalData;

    // Dowels
    dowelData?: DowelData;

    // Calculated
    volume: number;             // m³
    weight: number;             // kg
    bearingArea: number;        // m²
    requiredBearingCapacity: number; // kN/m²
}

// ==================== Enhanced Foundation Element ====================

export interface EnhancedFoundationElement extends Element {
    category: 'Structural Foundations';
    geometryData: EnhancedFoundationGeometryData;

    // Structural
    structuralMaterial: StructuralMaterial;
    rebarHostData: RebarHostData;

    // Loads (from connected column)
    designLoads?: {
        axialLoad: number;          // kN
        momentX: number;            // kN·m
        momentY: number;            // kN·m
        shearX: number;             // kN
        shearY: number;             // kN
    };

    // Soil data
    soilData?: {
        bearingCapacity: number;    // kN/m²
        soilType: string;
        waterTable?: number;        // m below grade
    };

    // Connected column
    supportedColumnId?: string;
}

// ==================== Tool Options ====================

export interface FoundationToolOptions {
    // Type
    foundationType: FoundationType;
    familyName: string;
    typeName: string;

    // Dimensions
    shape: FootingShape;
    width: number;
    length: number;
    thickness: number;

    // Level
    levelId: string;
    heightOffset: number;

    // Material
    materialId: string;

    // Pile cap options
    pileConfig?: PileConfiguration;
    pileDiameter?: number;
    pileSpacing?: number;

    // Stepped footing options
    steps?: StepData[];

    // Pedestal options
    pedestal?: PedestalData;

    // Dowel options
    dowels?: DowelData;

    // Auto-size
    autoSize: boolean;
    designAxialLoad?: number;
    soilBearingCapacity?: number;
}

// Standard pile configurations
export const PILE_CONFIGURATIONS: Record<PileConfiguration, (spacing: number) => [number, number][]> = {
    '2-Pile': (s) => [[-s / 2, 0], [s / 2, 0]],
    '3-Pile': (s) => [[0, s * 0.577], [-s / 2, -s * 0.289], [s / 2, -s * 0.289]],
    '4-Pile': (s) => [[-s / 2, -s / 2], [s / 2, -s / 2], [-s / 2, s / 2], [s / 2, s / 2]],
    '6-Pile': (s) => [
        [-s, -s / 2], [0, -s / 2], [s, -s / 2],
        [-s, s / 2], [0, s / 2], [s, s / 2]
    ],
    '9-Pile': (s) => [
        [-s, -s], [0, -s], [s, -s],
        [-s, 0], [0, 0], [s, 0],
        [-s, s], [0, s], [s, s]
    ],
    'Custom': () => []
};

// ==================== Foundation Tool Class ====================

export class IsolatedFoundationTool extends BaseTool {
    id = 'isolated_foundation';
    name = 'Isolated Foundation';
    cursor = 'crosshair';

    private options: FoundationToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): FoundationToolOptions {
        return {
            foundationType: 'Pad Footing',
            familyName: 'M_Footing-Rectangular',
            typeName: '1800 x 1800 x 450mm',
            shape: 'Rectangular',
            width: 1800,
            length: 1800,
            thickness: 450,
            levelId: 'Level 1',
            heightOffset: -450,     // Top at level, bottom below
            materialId: 'mat-concrete-30',
            autoSize: false,
            soilBearingCapacity: 150 // kN/m² default
        };
    }

    /**
     * Set foundation options from UI
     */
    setOptions(options: Partial<FoundationToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): FoundationToolOptions {
        return { ...this.options };
    }

    /**
     * Auto-size footing based on load and soil capacity
     */
    autoSizeFooting(axialLoad: number, bearingCapacity: number): { width: number; length: number } {
        // Factor of safety
        const FOS = 3;
        const requiredArea = (axialLoad * FOS) / bearingCapacity; // m²

        // Square footing
        const side = Math.sqrt(requiredArea) * 1000; // mm

        // Round to nearest 100mm
        const rounded = Math.ceil(side / 100) * 100;

        return { width: rounded, length: rounded };
    }

    /**
     * Calculate pile cap size based on configuration
     */
    calculatePileCapSize(config: PileConfiguration, pileDiameter: number, pileSpacing: number, edgeDistance: number): { width: number; length: number } {
        const positions = PILE_CONFIGURATIONS[config](pileSpacing);

        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;

        for (const [x, y] of positions) {
            minX = Math.min(minX, x);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
            maxY = Math.max(maxY, y);
        }

        const width = (maxX - minX) + pileDiameter + 2 * edgeDistance;
        const length = (maxY - minY) + pileDiameter + 2 * edgeDistance;

        return { width, length };
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        let snapped = this.snapToGrid(point);

        // Snap to column if nearby
        const column = this.findNearbyColumn(snapped);
        if (column) {
            snapped = new THREE.Vector3(...(column.geometryData as any).basePoint ||
                [(column.geometryData as any).position[0],
                (column.geometryData as any).position[1],
                    0]);
        }

        const foundation = this.createFoundation(snapped, column?.id);
        this.context.bimStore.addElement(foundation);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            const column = this.findNearbyColumn(snapped);

            this.context.appStore.setToolPreview({
                type: 'foundation_isolated',
                position: column ? new THREE.Vector3(...(column.geometryData as any).basePoint ||
                    [(column.geometryData as any).position[0],
                    (column.geometryData as any).position[1],
                        0]) : snapped,
                foundationType: this.options.foundationType,
                width: this.options.width / 1000,
                length: this.options.length / 1000,
                thickness: this.options.thickness / 1000,
                attachedToColumn: !!column
            });
        }
    }

    onPointerUp(): void { }

    private findNearbyColumn(point: THREE.Vector3): Element | null {
        if (!this.context) return null;

        const tolerance = 0.5; // 500mm

        for (const [, element] of this.context.bimStore.project.elements) {
            if (element.category !== 'Structural Columns') continue;

            const geom = element.geometryData as any;
            const colPos = geom.basePoint || geom.position;
            if (!colPos) continue;

            const colPoint = new THREE.Vector3(colPos[0], colPos[1], point.z);
            if (point.distanceTo(colPoint) < tolerance) {
                return element;
            }
        }

        return null;
    }

    private createFoundation(point: THREE.Vector3, supportedColumnId?: string): EnhancedFoundationElement {
        let width = this.options.width;
        let length = this.options.length;

        // Auto-size if enabled
        if (this.options.autoSize && this.options.designAxialLoad && this.options.soilBearingCapacity) {
            const size = this.autoSizeFooting(this.options.designAxialLoad, this.options.soilBearingCapacity);
            width = size.width;
            length = size.length;
        }

        // Calculate pile cap size if pile cap
        if (this.options.foundationType === 'Pile Cap' && this.options.pileConfig) {
            const pileSpacing = this.options.pileSpacing || 900;
            const pileDiameter = this.options.pileDiameter || 600;
            const edgeDistance = pileDiameter;
            const capSize = this.calculatePileCapSize(this.options.pileConfig, pileDiameter, pileSpacing, edgeDistance);
            width = capSize.width;
            length = capSize.length;
        }

        const thickness = this.options.thickness;
        const topElevation = point.z + (this.options.heightOffset / 1000);
        const bottomElevation = topElevation - (thickness / 1000);

        // Calculate volume and area
        let volume = 0;
        let bearingArea = 0;

        if (this.options.shape === 'Circular') {
            const radius = width / 2000;
            bearingArea = Math.PI * radius * radius;
            volume = bearingArea * (thickness / 1000);
        } else {
            bearingArea = (width / 1000) * (length / 1000);
            volume = bearingArea * (thickness / 1000);
        }

        // Add stepped volume if applicable
        if (this.options.steps) {
            for (const step of this.options.steps) {
                volume += (step.width / 1000) * (step.length / 1000) * (step.thickness / 1000);
            }
        }

        // Add pedestal volume if applicable
        if (this.options.pedestal?.enabled) {
            if (this.options.pedestal.shape === 'Circular') {
                const radius = this.options.pedestal.width / 2000;
                volume += Math.PI * radius * radius * (this.options.pedestal.height / 1000);
            } else {
                volume += (this.options.pedestal.width / 1000) *
                    (this.options.pedestal.length / 1000) *
                    (this.options.pedestal.height / 1000);
            }
        }

        const material = this.getMaterial(this.options.materialId);
        const weight = volume * material.properties.density;

        // Build pile data if pile cap
        let pileData: PileData | undefined;
        if (this.options.foundationType === 'Pile Cap' && this.options.pileConfig) {
            const pileSpacing = this.options.pileSpacing || 900;
            pileData = {
                configuration: this.options.pileConfig,
                pileType: `Bored Pile ${this.options.pileDiameter || 600}mm`,
                pileDiameter: this.options.pileDiameter || 600,
                pileLength: 15, // Default 15m
                pileSpacing: pileSpacing,
                edgeDistance: (this.options.pileDiameter || 600),
                pilePositions: PILE_CONFIGURATIONS[this.options.pileConfig](pileSpacing)
            };
        }

        const geometryData: EnhancedFoundationGeometryData = {
            foundationType: this.options.foundationType,
            position: point.toArray() as [number, number, number],
            rotation: 0,
            shape: this.options.shape,
            width,
            length,
            thickness,
            topElevation,
            bottomElevation,
            levelId: this.options.levelId,
            heightOffset: this.options.heightOffset,
            pileData,
            steppedData: this.options.steps ? { steps: this.options.steps } : undefined,
            pedestalData: this.options.pedestal,
            dowelData: this.options.dowels,
            volume,
            weight,
            bearingArea,
            requiredBearingCapacity: this.options.designAxialLoad ?
                (this.options.designAxialLoad * 3) / bearingArea : 0
        };

        const foundation: EnhancedFoundationElement = {
            id: uuidv4(),
            category: 'Structural Foundations',
            type: `${this.options.familyName}: ${width}x${length}x${thickness}mm`,
            parameters: this.buildParameters(geometryData),
            constraints: this.buildConstraints(),
            geometryData,
            structuralMaterial: material,
            rebarHostData: {
                validHost: true,
                coverSettings: {
                    top: 75,        // Larger cover for foundations
                    bottom: 75,
                    sides: 50
                }
            },
            soilData: {
                bearingCapacity: this.options.soilBearingCapacity || 150,
                soilType: 'Unknown'
            },
            supportedColumnId
        };

        return foundation;
    }

    private getMaterial(materialId: string): StructuralMaterial {
        const materials: Record<string, StructuralMaterial> = {
            'mat-concrete-30': {
                id: 'mat-concrete-30',
                name: 'Concrete - C30/37',
                type: 'Concrete',
                properties: {
                    youngsModulus: 30000,
                    shearModulus: 12500,
                    poissonRatio: 0.2,
                    density: 2400,
                    thermalExpansionCoefficient: 0.00001,
                    concreteCompression: 30
                }
            },
            'mat-concrete-25': {
                id: 'mat-concrete-25',
                name: 'Concrete - C25/30',
                type: 'Concrete',
                properties: {
                    youngsModulus: 28000,
                    shearModulus: 11500,
                    poissonRatio: 0.2,
                    density: 2400,
                    thermalExpansionCoefficient: 0.00001,
                    concreteCompression: 25
                }
            }
        };

        return materials[materialId] || materials['mat-concrete-30'];
    }

    private buildParameters(geom: EnhancedFoundationGeometryData): Record<string, any> {
        const params: Record<string, any> = {
            // Dimensions
            'Width': geom.width,
            'Length': geom.length,
            'Thickness': geom.thickness,
            'Shape': geom.shape,

            // Elevations
            'Top Elevation': geom.topElevation,
            'Bottom Elevation': geom.bottomElevation,
            'Level': geom.levelId,
            'Height Offset': geom.heightOffset,

            // Calculated
            'Volume': Math.round(geom.volume * 1000) / 1000,
            'Weight': Math.round(geom.weight),
            'Bearing Area': Math.round(geom.bearingArea * 100) / 100,
            'Required Bearing Capacity': Math.round(geom.requiredBearingCapacity)
        };

        // Pile cap parameters
        if (geom.pileData) {
            params['Pile Configuration'] = geom.pileData.configuration;
            params['Pile Diameter'] = geom.pileData.pileDiameter;
            params['Pile Spacing'] = geom.pileData.pileSpacing;
            params['Number of Piles'] = geom.pileData.pilePositions.length;
        }

        // Pedestal parameters
        if (geom.pedestalData?.enabled) {
            params['Pedestal Width'] = geom.pedestalData.width;
            params['Pedestal Length'] = geom.pedestalData.length;
            params['Pedestal Height'] = geom.pedestalData.height;
        }

        // Dowel parameters
        if (geom.dowelData?.enabled) {
            params['Dowel Size'] = geom.dowelData.barSize;
            params['Dowel Quantity'] = geom.dowelData.quantity;
            params['Dowel Embedment'] = geom.dowelData.embedmentDepth;
        }

        return params;
    }

    protected buildConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.levelId,
                data: {
                    offset: this.options.heightOffset
                }
            }
        ];
    }
}

// ==================== Helper Functions ====================

/**
 * Calculate required footing size
 */
export function calculateFootingSize(
    axialLoad: number,          // kN
    bearingCapacity: number,    // kN/m²
    safetyFactor: number = 3
): { width: number; length: number; thickness: number } {
    const requiredArea = (axialLoad * safetyFactor) / bearingCapacity;
    const side = Math.sqrt(requiredArea) * 1000;
    const rounded = Math.ceil(side / 100) * 100;

    // Thickness approximately 1/4 to 1/3 of width
    const thickness = Math.ceil(rounded / 4 / 50) * 50;

    return { width: rounded, length: rounded, thickness };
}

/**
 * Check bearing capacity adequacy
 */
export function checkBearingCapacity(
    foundation: EnhancedFoundationElement,
    axialLoad: number
): { adequate: boolean; utilizationRatio: number; requiredCapacity: number } {
    const bearingArea = foundation.geometryData.bearingArea;
    const appliedPressure = axialLoad / bearingArea; // kN/m²
    const allowableCapacity = foundation.soilData?.bearingCapacity || 150;

    return {
        adequate: appliedPressure <= allowableCapacity,
        utilizationRatio: appliedPressure / allowableCapacity,
        requiredCapacity: appliedPressure
    };
}
